<script lang="ts">
  import { intcomma, pluralize, widont } from 'journalize';

  import {
    DEMOCRAT,
    DEMOCRAT_LIGHT,
    GOP,
    GOP_LIGHT,
    NEUTRAL,
  } from '../colors.js';

  import {
    countyChoices,
    getVotesLeft,
    nameParts,
    shortCounty,
  } from '../utils/race-helpers.js';

  import type {
    CountyResult,
    Race,
    ReadableRaceStore,
  } from '../types';

  type LeadingParty = 'D' | 'R' | null;

  interface PartyData {
    demVotes: number;
    repVotes: number;
    margin: number;
    leadingParty: LeadingParty;
  }

  interface CountyEstimate {
    county: string;
    fipsCode: string;
    totalVotes: number;
    eevp: number;
    percentLeft: number;
    votesLeft: number;
    demVotes: number;
    repVotes: number;
    partyMargin: number;
    leadingParty: LeadingParty;
  }

  interface ChartCountyTarget extends CountyEstimate {
    targetX: number;
    targetY: number;
    radius: number;
    fill: string;
    stroke: string;
    labelColor: string;
    labelLines: string[];
  }

  interface ChartCountyPlaced extends ChartCountyTarget {
    x: number;
    y: number;
  }

  interface TableRow {
    county: string;
    fipsCode: string;
    votesLeft: number | null;
    percentLeft: number | null;
    leadingParty: LeadingParty;
  }

  interface RankedTableRow extends TableRow {
    estimatedTotalVotes: number;
    displayOrder: number;
  }

  // Rows shown before Show more in the bubble chart's table.
  const TOP_TABLE_COUNT = 10;

  const featuredCountyNames = [
    'Los Angeles',
    'Orange',
    'Ventura',
    'San Bernardino',
    'Riverside',
  ];

  export let feed: ReadableRaceStore;
  export let tableOnly = false;

  $: data = $feed;
  $: title = data.attributes.title;

  // EEVP can be prefilled before actual votes are reported.
  $: hasResults = (data?.attributes?.countyResults ?? []).some(
    (county) => Number(county.totalVotes) > 0
  );

  const width = 1200;
  const height = 750;

  const margin = {
    top: 30,
    right: 20,
    bottom: 20,
    left: 30,
  };

  const minRadius = 10;
  const maxRadius = 108;
  const collisionPadding = 2;

  const LABEL_FONT_SIZE = 16;
  const TOOLTIP_FLIP_Y = 120;

  const SHARE_LEFT_GROUPS = [
    { label: 'less than 25%', min: 0, max: 25 },
    { label: '25% to 50%', min: 25, max: 50 },
    { label: '50% to 75%', min: 50, max: 75 },
    { label: '75% or more', min: 75, max: Infinity },
  ];

  const SUMMARY_TOP_COUNT = 3;

  let hoveredCounty: ChartCountyPlaced | null = null;

  function formatVotes(value: number) {
    return intcomma(Math.round(value));
  }

  function countiesText(count: number) {
    return `${count} ${pluralize(count, 'counties', 'county')}`;
  }

  function hasHave(count: number) {
    return pluralize(count, 'have', 'has');
  }

  function listText(items: string[]) {
    if (items.length <= 1) return items.join('');

    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
  }

  // Last name for the legend and table; full name and party for the screen reader summary
  // the widthParty optional param is for adding (party) at the end of the name, the way that we want it for the screen reader
  function candidateName(
    race: Race,
    party: 'D' | 'R',
    { withParty = false } = {}
  ) {
    const choice = race.attributes.choices.find(
      (candidate) => candidate.party?.abbr === party
    );

    if (!choice) {
      return party === 'D' ? 'Democrat' : 'Republican';
    }

    const { first, middle, last } = nameParts(choice);

    if (!withParty) return last;

    const name = [first, middle, last].filter(Boolean).join(' ');

    return `${name} (${choice.party?.name})`;
  }

  // Screen reader summary

  function buildChartSummary(
    race: Race,
    tableCounties: CountyEstimate[]
  ) {
    const introBeforeResults =
      `A scatterplot shows the estimated votes remaining in California's race for ${title}.`;

    const introWithResults =
      tableCounties.length < 58
        ? `A scatterplot shows the estimated votes remaining in the ${countiesText(tableCounties.length)} that ${hasHave(tableCounties.length)} reported results so far in the race for ${title}.`
        : `A scatterplot shows the estimated votes remaining across California's 58 counties in the race for ${title}.`;

    if (tableCounties.length === 0) {
      return `${introBeforeResults} No results are available yet.`;
    }

    if (
      tableCounties.length === 58 &&
      tableCounties.every((c) => c.eevp >= 100)
    ) {
      return `All votes in the race for ${title} have been counted.`;
    }

    const allAt99 =
      tableCounties.length === 58 &&
      tableCounties.every((c) => c.eevp === 99);

    const sentences = [introWithResults];

    if (allAt99) {
      sentences.push(
        'All counties have reported 99% of their estimated expected vote. This estimated percentage will stay fixed until results are certified.'
      );
    } else {
      const groups = SHARE_LEFT_GROUPS.map(
        ({ label, min, max }) => ({
          label,
          count: tableCounties.filter(
            (c) => c.percentLeft >= min && c.percentLeft < max
          ).length,
        })
      )
        .filter(({ count }) => count > 0)
        .map(({ label, count }, i) =>
          i === 0
            ? `${countiesText(count)} ${hasHave(count)} ${label} of votes remaining`
            : `${count} ${hasHave(count)} ${label}`
        );

      sentences.push(`${listText(groups)}.`);
    }

    const repLeads = tableCounties.filter(
      (c) => c.leadingParty === 'R'
    ).length;

    const demLeads = tableCounties.filter(
      (c) => c.leadingParty === 'D'
    ).length;

    if (repLeads + demLeads > 0) {
      const [first, second] = [
        {
          name: candidateName(race, 'R', { withParty: true }),
          count: repLeads,
        },
        {
          name: candidateName(race, 'D', { withParty: true }),
          count: demLeads,
        },
      ].sort((a, b) => b.count - a.count);

      sentences.push(
        `${first.name} leads in ${countiesText(first.count)} and ${second.name} leads in ${countiesText(second.count)}.`
      );
    }

    const totalLeft = tableCounties.reduce(
      (sum, c) => sum + c.votesLeft,
      0
    );

    const top = [...tableCounties]
      .sort((a, b) => b.votesLeft - a.votesLeft)
      .slice(0, SUMMARY_TOP_COUNT)
      .map((c) => `${c.county} (${formatVotes(c.votesLeft)})`);

    sentences.push(
      tableCounties.length < 58
        ? `An estimated ${formatVotes(totalLeft)} votes remain in the ${countiesText(tableCounties.length)} reporting so far.`
        : `An estimated ${formatVotes(totalLeft)} votes remain statewide.`
    );

    if (!allAt99) {
      sentences.push(
        `The counties with the most votes left are ${listText(top)}.`
      );
    }

    const notReported = 58 - tableCounties.length;

    if (notReported > 0) {
      sentences.push(
        `${countiesText(notReported)} ${pluralize(notReported, "haven't", "hasn't")} reported results yet.`
      );
    }

    return sentences.join(' ');
  }

  // County labels.

  function getCountyLabelLines(name: string) {
    const words = name.trim().split(/\s+/);

    if (words.length <= 1) {
      return [name];
    }

    if (words.length === 2) {
      return [words[0], words[1]];
    }

    const splitIndex = Math.ceil(words.length / 2);

    return [
      words.slice(0, splitIndex).join(' '),
      words.slice(splitIndex).join(' '),
    ];
  }

  function canFitLabel(radius: number, labelLines: string[]) {
    if (!labelLines?.length || radius <= 0) {
      return false;
    }

    const longestLine = Math.max(
      ...labelLines.map((line) => line.length)
    );

    const textWidth = longestLine * LABEL_FONT_SIZE * 0.6;

    const textHeight =
      labelLines.length === 1
        ? LABEL_FONT_SIZE
        : LABEL_FONT_SIZE * 1.9;

    return (
      textWidth <= radius * 1.7 &&
      textHeight <= radius * 1.7
    );
  }

  // Colors.

  function getLabelColor(leadingParty: LeadingParty) {
    if (leadingParty === 'D') return DEMOCRAT;
    if (leadingParty === 'R') return GOP;

    return '#666666';
  }

  function getVotesBarColor(leadingParty: LeadingParty) {
    if (leadingParty === 'D') return DEMOCRAT_LIGHT;
    if (leadingParty === 'R') return GOP_LIGHT;

    return '#cccccc';
  }

  function getCircleColors(leadingParty: LeadingParty) {
    if (leadingParty === 'D') {
      return {
        fill: DEMOCRAT_LIGHT,
        stroke: DEMOCRAT,
      };
    }

    if (leadingParty === 'R') {
      return {
        fill: GOP_LIGHT,
        stroke: GOP,
      };
    }

    return {
      fill: NEUTRAL,
      stroke: '#aaaaaa',
    };
  }

  // Vote estimates.

  function getPercentLeft(eevp: number) {
    return Math.max(
      0,
      Math.min(100, 100 - Number(eevp))
    );
  }

  function hasUsableEstimate(county: CountyResult) {
    if (
      county.totalVotes == null ||
      county.eevp == null
    ) {
      return false;
    }

    const votes = Number(county.totalVotes);
    const eevp = Number(county.eevp);

    return (
      Number.isFinite(votes) &&
      votes >= 0 &&
      Number.isFinite(eevp) &&
      eevp > 0 &&
      eevp <= 100
    );
  }

  function getPartyData(
    race: Race,
    county: CountyResult
  ): PartyData {
    let demVotes = 0;
    let repVotes = 0;

    for (const { choice, votes } of countyChoices(race, county)) {
      if (choice.party?.abbr === 'D') {
        demVotes += votes;
      }

      if (choice.party?.abbr === 'R') {
        repVotes += votes;
      }
    }

    const twoPartyTotal = demVotes + repVotes;

    if (twoPartyTotal === 0) {
      return {
        demVotes,
        repVotes,
        margin: 0,
        leadingParty: null,
      };
    }

    const partyMargin =
      (repVotes - demVotes) / twoPartyTotal;

    let leadingParty: LeadingParty = null;

    if (demVotes > repVotes) {
      leadingParty = 'D';
    } else if (repVotes > demVotes) {
      leadingParty = 'R';
    }

    return {
      demVotes,
      repVotes,
      margin: partyMargin,
      leadingParty,
    };
  }

  // Chart positioning and size.

  function getX(partyMargin: number) {
    const centerX = width / 2;

    const availableWidth =
      width - margin.left - margin.right;

    const maxSpread = availableWidth * 0.43;

    return centerX + partyMargin * maxSpread;
  }

  function getY(percentLeft: number) {
    const chartHeight =
      height - margin.top - margin.bottom;

    return (
      margin.top +
      (1 - percentLeft / 100) * chartHeight
    );
  }

  function getRadius(votesLeft: number, maxVotesLeft: number) {
    if (
      !Number.isFinite(votesLeft) ||
      votesLeft <= 0 ||
      maxVotesLeft <= 0
    ) {
      return minRadius;
    }

    const normalized =
      0.025 * Math.sqrt(votesLeft / maxVotesLeft);

    const scaled = Math.pow(normalized, 0.35);

    return minRadius + scaled * (maxRadius - minRadius);
  }

  function constrainNode(node: ChartCountyPlaced) {
    const chartLeft =
      margin.left + node.radius + collisionPadding;

    const chartRight =
      width - margin.right - node.radius - collisionPadding;

    const chartTop =
      margin.top + node.radius + collisionPadding;

    const chartBottom =
      height - margin.bottom - node.radius - collisionPadding;

    const centerX = width / 2;

    let minX = chartLeft;
    let maxX = chartRight;

    if (node.leadingParty === 'D') {
      maxX = centerX - node.radius - collisionPadding;
    }

    if (node.leadingParty === 'R') {
      minX = centerX + node.radius + collisionPadding;
    }

    node.x = Math.max(
      minX,
      Math.min(maxX, node.x)
    );

    node.y = Math.max(
      chartTop,
      Math.min(chartBottom, node.y)
    );
  }

  function resolveCollisions(
    inputNodes: ChartCountyTarget[]
  ): ChartCountyPlaced[] {
    const nodes = inputNodes.map(
      (node, index): ChartCountyPlaced => {
        const angle = index * 2.399963229728653;
        const initialOffset = (index % 5) * 0.15;

        return {
          ...node,
          x:
            node.targetX +
            Math.cos(angle) * initialOffset,
          y:
            node.targetY +
            Math.sin(angle) * initialOffset,
        };
      }
    );

    const iterations = 350;

    for (
      let iteration = 0;
      iteration < iterations;
      iteration += 1
    ) {
      for (const node of nodes) {
        node.x += (node.targetX - node.x) * 0.008;
        node.y += (node.targetY - node.y) * 0.06;

        constrainNode(node);
      }

      for (let i = 0; i < nodes.length; i += 1) {
        for (let j = i + 1; j < nodes.length; j += 1) {
          const a = nodes[i];
          const b = nodes[j];

          let dx = b.x - a.x;
          let dy = b.y - a.y;

          let distance = Math.sqrt(
            dx * dx + dy * dy
          );

          const minimumDistance =
            a.radius + b.radius + collisionPadding;

          if (distance < minimumDistance) {
            if (distance < 0.0001) {
              const angle =
                (i * 31 + j * 17) * 0.618;

              dx = Math.cos(angle);
              dy = Math.sin(angle);
              distance = 1;
            }

            const overlap = minimumDistance - distance;

            const pushX =
              (dx / distance) * overlap * 0.75;

            const pushY =
              (dy / distance) * overlap * 0.35;

            a.x -= pushX;
            a.y -= pushY;

            b.x += pushX;
            b.y += pushY;

            constrainNode(a);
            constrainNode(b);
          }
        }
      }

      for (const node of nodes) {
        constrainNode(node);
      }
    }

    for (let pass = 0; pass < 150; pass += 1) {
      let foundCollision = false;

      for (let i = 0; i < nodes.length; i += 1) {
        for (let j = i + 1; j < nodes.length; j += 1) {
          const a = nodes[i];
          const b = nodes[j];

          let dx = b.x - a.x;
          let dy = b.y - a.y;

          let distance = Math.sqrt(
            dx * dx + dy * dy
          );

          const minimumDistance =
            a.radius + b.radius + collisionPadding;

          if (distance < minimumDistance) {
            foundCollision = true;

            if (distance < 0.0001) {
              const angle =
                (i * 31 + j * 17) * 0.618;

              dx = Math.cos(angle);
              dy = Math.sin(angle);
              distance = 1;
            }

            const overlap = minimumDistance - distance;

            const pushX =
              (dx / distance) * overlap * 0.51;

            const pushY =
              (dy / distance) * overlap * 0.51;

            a.x -= pushX;
            a.y -= pushY;

            b.x += pushX;
            b.y += pushY;

            constrainNode(a);
            constrainNode(b);
          }
        }
      }

      if (!foundCollision) {
        break;
      }
    }

    return nodes;
  }

  // All counties with usable estimates, including completed counties.
  $: estimatedCounties = (
    data?.attributes?.countyResults ?? []
  ).filter(hasUsableEstimate);

  // Every county has an eevp, so the total isn't partial
  $: allCountiesReported = estimatedCounties.length === 58;

  // Remaining votes include votes for all candidates.
  $: totalVotesLeft = estimatedCounties.reduce(
    (sum, county) =>
      sum + getVotesLeft(county.totalVotes, county.eevp),
    0
  );

  $: totalCountedVotes = estimatedCounties.reduce(
    (sum, county) => sum + Number(county.totalVotes),
    0
  );

  $: totalEstimatedVotes =
    totalCountedVotes + totalVotesLeft;

  $: totalPercentLeft =
    estimatedCounties.length > 0 && totalEstimatedVotes > 0
      ? (totalVotesLeft / totalEstimatedVotes) * 100
      : null;

  $: estimatedTableCounties = estimatedCounties.map(
    (county): CountyEstimate => {
      const partyData = getPartyData(data, county);

      return {
        county: shortCounty(county.county),
        fipsCode: county.fipsCode,
        totalVotes: Number(county.totalVotes),
        eevp: Number(county.eevp),
        percentLeft: getPercentLeft(county.eevp),
        votesLeft: getVotesLeft(
          county.totalVotes,
          county.eevp
        ),
        demVotes: partyData.demVotes,
        repVotes: partyData.repVotes,
        partyMargin: partyData.margin,
        leadingParty: partyData.leadingParty,
      };
    }
  );

  // Bubble chart data.
  $: counties = estimatedTableCounties.filter(
    (county) => county.votesLeft > 0
  );

  $: highestNumberOfVotesLeft = Math.max(
    0,
    ...counties.map((county) => county.votesLeft)
  );

  $: targetData = (tableOnly ? [] : counties)
    .filter((county) => county.demVotes + county.repVotes > 0)
    .map((county): ChartCountyTarget => {
      const colors = getCircleColors(county.leadingParty);

      return {
        ...county,
        targetX: getX(county.partyMargin),
        targetY: getY(county.percentLeft),
        radius: getRadius(
          county.votesLeft,
          highestNumberOfVotesLeft
        ),
        fill: colors.fill,
        stroke: colors.stroke,
        labelColor: getLabelColor(county.leadingParty),
        labelLines: getCountyLabelLines(
          county.county === 'Los Angeles' ? 'L.A.' : county.county
        ),
      };
    });

  $: chartData = tableOnly ? [] : resolveCollisions(targetData);

  // Five selected counties.
  // Sort by estimated total votes, not votes still uncounted.
  let featuredTableRows: RankedTableRow[];

  $: featuredTableRows = featuredCountyNames.map(
    (name, displayOrder): RankedTableRow => {
      const county = estimatedTableCounties.find(
        (county) => county.county === name
      );

      return {
        county: name,
        fipsCode: county?.fipsCode ?? `featured-${displayOrder}`,
        votesLeft: county?.votesLeft ?? null,
        percentLeft: county?.percentLeft ?? null,
        leadingParty: county?.leadingParty ?? null,
        estimatedTotalVotes: county
          ? county.totalVotes + county.votesLeft
          : -1,
        displayOrder,
      };
    }
  );

  // Bubble chart's table: every county ranked by votes left.
  $: rankedTableCounties = [...estimatedTableCounties].sort(
    (a, b) =>
      b.votesLeft - a.votesLeft ||
      a.county.localeCompare(b.county)
  );

  // Table only: all counties outside the selected five.
  $: otherTableCounties = estimatedTableCounties.filter(
    (county) => !featuredCountyNames.includes(county.county)
  );

  $: otherVotesLeft = otherTableCounties.reduce(
    (sum, county) => sum + county.votesLeft,
    0
  );

  $: otherEstimatedVotes = otherTableCounties.reduce(
    (sum, county) =>
      sum + county.totalVotes + county.votesLeft,
    0
  );

  let showAllCounties = false;
  let tableOnlyRows: TableRow[];
  let chartTableRows: TableRow[];

  // Before results: only the All counties total row.
  // After results: five selected counties plus All other counties.
  $: tableOnlyRows = hasResults
    ? [
        ...[...featuredTableRows].sort(
          (a, b) =>
            b.estimatedTotalVotes - a.estimatedTotalVotes ||
            a.displayOrder - b.displayOrder
        ),
        {
          county: 'All other counties',
          fipsCode: 'all-other-counties',
          votesLeft:
            otherTableCounties.length > 0
              ? otherVotesLeft
              : null,
          percentLeft:
            otherEstimatedVotes > 0
              ? (otherVotesLeft / otherEstimatedVotes) * 100
              : null,
          leadingParty: null,
        },
      ]
    : [];

  // Bubble chart's mobile table: the top counties by votes left,
  // and Show more reveals the rest.
  $: chartTableRows = hasResults
    ? showAllCounties
      ? rankedTableCounties
      : rankedTableCounties.slice(0, TOP_TABLE_COUNT)
    : [];

  $: hiddenCountyCount = rankedTableCounties.length - TOP_TABLE_COUNT;

  $: centerX = width / 2;

  $: chartSummary = tableOnly
    ? ''
    : buildChartSummary(data, estimatedTableCounties);
</script>

<div
  class="estimated-votes-remaining text-sans-serif"
  class:table-only={tableOnly}
>
  <div class="chart-header">
    <div class="chart-title text-xl text-sans-serif">
      {#if !allCountiesReported}
        {widont('Estimated votes remaining in governor’s race')}
      {:else}
        {widont(
          `There are an estimated ${intcomma(totalVotesLeft)} votes remaining in the governor’s race`
        )}
      {/if}
    </div>

    {#if !tableOnly}
      <div
        class="circle-size-legend text-sm text-sans-serif"
        aria-hidden="true"
      >
        <svg
          class="circle-size-legend-symbol"
          viewBox="0 0 68 28"
          aria-hidden="true"
        >
          <circle
            cx="10"
            cy="18"
            r="5"
            fill="#dddddd"
            stroke="#777777"
          />

          <circle
            cx="30"
            cy="15"
            r="8"
            fill="#dddddd"
            stroke="#777777"
          />

          <circle
            cx="55"
            cy="12"
            r="11"
            fill="#dddddd"
            stroke="#777777"
          />
        </svg>

        <span>Larger circles mean more votes left to count</span>
      </div>

      <div class="party-labels" aria-hidden="true">
        <div class="party-label text-sm text-sans-serif">
          ← Democrats lead
        </div>

        <div class="party-label text-sm text-sans-serif">
          Republicans lead →
        </div>
      </div>
    {/if}

    {#if !hasResults}
      <div class="awaiting-results text-sm text-sans-serif">
        Polls are still open. Check back later.
      </div>
    {/if}
  </div>

  {#if !tableOnly}
    <div class="chart-layout">
      <div class="chart-area">
        <p class="visually-hidden">{chartSummary}</p>

        <div
          class="center-axis-label center-axis-label--top text-sm text-sans-serif"
          aria-hidden="true"
        >
          More votes left

          <span class="center-axis-unit text-sm text-sans-serif">
            (% of estimated expected vote)
          </span>
        </div>

        <div class="county-chart-wrap">
          <svg
            class="county-chart text-sans-serif"
            viewBox={`0 0 ${width} ${height}`}
            aria-hidden="true"
          >
            <g class="center-axis" pointer-events="none">
              <line
                x1={centerX}
                x2={centerX}
                y1="4"
                y2={height - 4}
                stroke="#777777"
                stroke-width="1.5"
                vector-effect="non-scaling-stroke"
              />

              <path
                d={`M ${centerX - 4} 9 L ${centerX} 4 L ${centerX + 4} 9`}
                fill="none"
                stroke="#777777"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                vector-effect="non-scaling-stroke"
              />

              <path
                d={`M ${centerX - 4} ${height - 9} L ${centerX} ${height - 4} L ${centerX + 4} ${height - 9}`}
                fill="none"
                stroke="#777777"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                vector-effect="non-scaling-stroke"
              />
            </g>

            {#each chartData as county (county.fipsCode)}
              <g>
                <circle
                  cx={county.x}
                  cy={county.y}
                  r={county.radius}
                  fill={county.fill}
                  stroke={county.stroke}
                  stroke-width="1.5"
                  class="county-circle"
                  on:mouseenter={() => {
                    hoveredCounty = county;
                  }}
                  on:mouseleave={() => {
                    hoveredCounty = null;
                  }}
                />

                {#if canFitLabel(county.radius, county.labelLines)}
                  {@const lineHeight = LABEL_FONT_SIZE * 0.9}

                  <g class="county-label">
                    {#if county.labelLines.length === 1}
                      <text
                        class="text-base text-sans-serif"
                        x={county.x}
                        y={county.y}
                        fill={county.labelColor}
                        text-anchor="middle"
                        dominant-baseline="middle"
                        pointer-events="none"
                      >
                        {county.labelLines[0]}
                      </text>
                    {:else}
                      <text
                        class="text-base text-sans-serif"
                        x={county.x}
                        y={county.y}
                        fill={county.labelColor}
                        text-anchor="middle"
                        pointer-events="none"
                      >
                        <tspan
                          x={county.x}
                          y={county.y - lineHeight / 2}
                          text-anchor="middle"
                          dominant-baseline="middle"
                        >
                          {county.labelLines[0]}
                        </tspan>

                        <tspan
                          x={county.x}
                          y={county.y + lineHeight / 2}
                          text-anchor="middle"
                          dominant-baseline="middle"
                        >
                          {county.labelLines[1]}
                        </tspan>
                      </text>
                    {/if}
                  </g>
                {/if}
              </g>
            {/each}
          </svg>

          {#if hoveredCounty}
            {@const placeBelow =
              hoveredCounty.y - hoveredCounty.radius < TOOLTIP_FLIP_Y}

            {@const anchorY = placeBelow
              ? hoveredCounty.y + hoveredCounty.radius
              : hoveredCounty.y - hoveredCounty.radius}

            {@const align =
              hoveredCounty.x < width * 0.2
                ? 'start'
                : hoveredCounty.x > width * 0.8
                  ? 'end'
                  : 'center'}

            <div
              class={`county-tooltip county-tooltip--${align}`}
              class:county-tooltip--below={placeBelow}
              style:left={`${(hoveredCounty.x / width) * 100}%`}
              style:top={`${(anchorY / height) * 100}%`}
              style:border-color={hoveredCounty.stroke}
              aria-hidden="true"
            >
              <div class="tooltip-name text-sm text-sans-serif">
                {hoveredCounty.county}
              </div>

              <div class="tooltip-votes text-xs text-sans-serif">
                {formatVotes(hoveredCounty.votesLeft)} votes left
              </div>
            </div>
          {/if}
        </div>

        <div
          class="center-axis-label center-axis-label--bottom text-sm text-sans-serif"
          aria-hidden="true"
        >
          Fewer votes left
        </div>
      </div>
    </div>
  {/if}

  <div class="mobile-table eevp-county-key">
    {#if !tableOnly}
      <div class="eevp-county-key__legend text-sm text-sans-serif">
        <span class="eevp-county-key__item">
          <span
            class="eevp-county-key__chip eevp-county-key__chip--dem"
            aria-hidden="true"
          ></span>

          <span>{candidateName(data, 'D')} leading</span>
        </span>

        <span class="eevp-county-key__item">
          <span
            class="eevp-county-key__chip eevp-county-key__chip--gop"
            aria-hidden="true"
          ></span>

          <span>{candidateName(data, 'R')} leading</span>
        </span>
      </div>
    {/if}

    <table
      class="text-sm"
      aria-label="Estimated votes remaining in gubernatorial race, by county"
    >
      <thead>
        <tr>
          <th scope="col" class="text-sans-serif">
            County
          </th>

          <th scope="col" class="text-sans-serif">
            <span aria-hidden="true">Left (%)</span>

            <span class="visually-hidden">
              Percent of votes left
            </span>
          </th>

          <th scope="col" class="text-sans-serif">
            Votes left
          </th>
        </tr>
      </thead>

      <tbody>
        <tr class="mobile-total-row">
          <th scope="row" class="text-sans-serif">
            {#if allCountiesReported}
              All counties
            {:else}
              {countiesText(estimatedCounties.length)} reporting
            {/if}
          </th>

          <td class="text-sans-serif">
            {#if !hasResults}
              100%
            {:else if totalPercentLeft !== null}
              {totalPercentLeft.toFixed(1)}%
            {:else}
              <span aria-hidden="true">—</span><span class="visually-hidden">No data</span>
            {/if}
          </td>

          <td class="text-sans-serif">
            <div class="votes-left-cell">
              <span class="votes-left-number">
                {#if hasResults && estimatedCounties.length > 0}
                  {formatVotes(totalVotesLeft)}
                {:else}
                  <span aria-hidden="true">—</span><span class="visually-hidden">No data</span>
                {/if}
              </span>
            </div>
          </td>
        </tr>

        {#each tableOnly ? tableOnlyRows : chartTableRows as county (county.fipsCode)}
          <tr>
            <th scope="row" class="text-sans-serif">
              <div class="mobile-county-name">
                <span class="eevp-county-key__name">
                  {#if !tableOnly && county.leadingParty !== null}
                    <span
                      class="eevp-county-key__chip"
                      class:eevp-county-key__chip--dem={county.leadingParty === 'D'}
                      class:eevp-county-key__chip--gop={county.leadingParty === 'R'}
                      aria-hidden="true"
                    ></span>
                  {/if}

                  {#if !tableOnly && county.leadingParty !== null}
                    <!-- One string for screen readers -->
                    <span aria-hidden="true">{county.county}</span>

                    <span class="visually-hidden">
                      {`${county.county}, ${candidateName(data, county.leadingParty)} leading`}
                    </span>
                  {:else}
                    <span>{county.county}</span>
                  {/if}
                </span>
              </div>
            </th>

            <td class="text-sans-serif">
              {#if county.percentLeft !== null}
                {county.percentLeft.toFixed(1)}%
              {:else}
                <span aria-hidden="true">—</span><span class="visually-hidden">No data</span>
              {/if}
            </td>

            <td class="text-sans-serif">
              <div class="votes-left-cell">
                <span class="votes-left-number">
                  {#if county.votesLeft !== null}
                    {formatVotes(county.votesLeft)}
                  {:else}
                    <span aria-hidden="true">—</span><span class="visually-hidden">No data</span>
                  {/if}
                </span>
              </div>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>

    {#if !tableOnly && hasResults && hiddenCountyCount > 0}
      <button
        type="button"
        class="mobile-show-more text-sm text-sans-serif"
        aria-expanded={showAllCounties}
        on:click={() => {
          showAllCounties = !showAllCounties;
        }}
      >
        {showAllCounties
          ? 'Show less'
          : `Show more (${hiddenCountyCount} more counties)`}
      </button>
    {/if}
  </div>
</div>